import type { LabelWithRole, Review } from '@skop-app/lib-domain/types'
import { ReviewStatus } from '@skop-app/lib-types'

import type {
  OptionQueryKey,
  OptionRow,
  OptionUpdateInput,
} from '@/services/property/property.type'
import type { ReviewMessageParams } from '@/types'
import type { UpsertOptionVariables } from './option.mutation'
import {
  OPTION_ARCHIVED_STATUS,
  OPTION_RETURNED_STATUS,
  type OptionWorkflowStatus,
} from './option.status'

type UpsertOptionFn = (variables: UpsertOptionVariables) => Promise<unknown>
type UpsertReviewFn = (variables: {
  upsertType: 'update'
  input: Partial<Review>
}) => Promise<unknown>
type SubmitReviewInteractionFn = (
  params: ReviewMessageParams,
) => Promise<unknown>

const isCreationReview = (review: Review) =>
  review.metadata?.some(
    (metadata) =>
      metadata.key === 'isCreationReview' && metadata.value === 'true',
  )

export const findOptionCreationReview = ({
  elementId,
  reviewsById,
}: {
  elementId?: string
  reviewsById?: Record<string, Review[]>
}): Review | undefined => {
  if (!elementId) return undefined

  return (reviewsById?.[elementId] ?? []).find(isCreationReview)
}

export const getOptionCreationReviewLatestMessage = ({
  elementId,
  reviewsById,
}: {
  elementId?: string
  reviewsById?: Record<string, Review[]>
}) => {
  const creationReview = findOptionCreationReview({
    elementId,
    reviewsById,
  })
  const messages = creationReview?.messages?.items ?? []
  const latestMessage = messages[messages.length - 1]
  return latestMessage?.message?.trim() || undefined
}

// A validation refusal asks the creator to adjust or resubmit the option.
// It must not create new Option.status=REJECTED records.
export const resolveOptionRejectionStatus = (): typeof OPTION_RETURNED_STATUS =>
  OPTION_RETURNED_STATUS

export const resolveOptionModerationTransition = ({
  nextReviewStatus,
  nextStatus,
}: {
  listOwnerOrganizationID?: string
  nextReviewStatus?: ReviewStatus
  nextStatus: OptionWorkflowStatus
  optionOrganizationID?: string
}): {
  optionStatus: OptionWorkflowStatus
  reviewStatus?: ReviewStatus
} => {
  if (nextStatus === OPTION_ARCHIVED_STATUS || nextStatus === 'DELETED') {
    return {
      optionStatus: nextStatus,
    }
  }

  const optionStatus =
    nextStatus === ReviewStatus.REJECTED
      ? resolveOptionRejectionStatus()
      : nextStatus

  return {
    optionStatus,
    reviewStatus:
      nextReviewStatus ??
      (optionStatus === OPTION_RETURNED_STATUS
        ? ReviewStatus.PENDING
        : (optionStatus as ReviewStatus)),
  }
}

const syncCreationReviewStatus = async ({
  creationReview,
  nextStatus,
  upsertReview,
}: {
  creationReview?: Review
  nextStatus: ReviewStatus
  upsertReview: UpsertReviewFn
}) => {
  if (!creationReview?.id) return false

  await upsertReview({
    upsertType: 'update',
    input: {
      id: creationReview.id,
      status: nextStatus,
    },
  })

  return true
}

export const syncOptionStatusWithReview = async ({
  listOwnerOrganizationID,
  rows,
  nextStatus,
  nextReviewStatus,
  queryKey,
  reviewsById,
  upsertOption,
  upsertReview,
}: {
  listOwnerOrganizationID?: string
  rows: OptionRow[]
  nextStatus: OptionWorkflowStatus
  nextReviewStatus?: ReviewStatus
  queryKey: OptionQueryKey
  reviewsById?: Record<string, Review[]>
  upsertOption: UpsertOptionFn
  upsertReview: UpsertReviewFn
}) => {
  await Promise.all(
    rows.map(async (row) => {
      if (!row.id || row.id.startsWith('new-')) return

      const { optionStatus, reviewStatus } = resolveOptionModerationTransition({
        listOwnerOrganizationID,
        nextReviewStatus,
        nextStatus,
        optionOrganizationID: row.organizationID,
      })

      await upsertOption({
        upsertType: 'update',
        input: {
          id: row.id,
          status: optionStatus,
        },
        queryKey,
      })

      if (optionStatus === OPTION_ARCHIVED_STATUS || optionStatus === 'DELETED')
        return

      const creationReview = findOptionCreationReview({
        elementId: row.id,
        reviewsById,
      })

      if (!reviewStatus) {
        console.info('Option status updated without creation review sync', {
          optionId: row.id,
          status: optionStatus,
        })
        return
      }

      const didSyncReview = await syncCreationReviewStatus({
        creationReview,
        nextStatus: reviewStatus,
        upsertReview,
      })

      if (!didSyncReview) {
        console.info('Option status updated without creation review', {
          optionId: row.id,
          status: optionStatus,
        })
      }
    }),
  )
}

export const resubmitOptionWithReview = async ({
  input,
  queryKey,
  reviewsById,
  upsertOption,
  upsertReview,
  submitReviewInteraction,
  message,
  mentions,
}: {
  input: OptionUpdateInput
  queryKey: OptionQueryKey
  reviewsById?: Record<string, Review[]>
  upsertOption: UpsertOptionFn
  upsertReview: UpsertReviewFn
  submitReviewInteraction?: SubmitReviewInteractionFn
  message?: string
  mentions?: LabelWithRole[]
}) => {
  await upsertOption({
    upsertType: 'update',
    input,
    queryKey,
  })

  const creationReview = findOptionCreationReview({
    elementId: input.id,
    reviewsById,
  })
  const didSyncReview = await syncCreationReviewStatus({
    creationReview,
    nextStatus: ReviewStatus.PENDING,
    upsertReview,
  })

  if (!didSyncReview) {
    console.info('Option resubmitted without creation review', {
      optionId: input.id,
    })
    return
  }

  if (submitReviewInteraction && creationReview && message) {
    await submitReviewInteraction({
      review: {
        ...creationReview,
        status: ReviewStatus.PENDING,
      } as Review,
      mode: 'reply',
      message,
      mentions,
    })
  }
}
